<template>
  <Teleport to="body">
    <dialog
      ref="dialog"
      class="email-link-reminder"
      aria-labelledby="email-link-title"
      @cancel.prevent="dismiss"
    >
      <h3 id="email-link-title">{{ t(actionKey) }}</h3>
      <p>
        {{
          t(
            profile.emailAction === 'attach'
              ? 'emailLink.siteAccess'
              : 'emailLink.verifyAccess',
            { email: profile.profile?.email },
          )
        }}
      </p>
      <BaseButton
        monochrome
        block
        data-testid="email-link-action"
        @click="edit"
        >{{ t(actionKey) }}</BaseButton
      >
      <BaseButton
        monochrome
        block
        variant="ghost"
        data-testid="email-link-later"
        @click="dismiss"
        >{{ t('emailLink.later') }}</BaseButton
      >
    </dialog>
  </Teleport>
  <EmailVerificationModal
    v-if="editing && eligible && profile.emailAction === 'verify'"
    :pending="false"
    error=""
    @close="editing = false"
    @verified="editing = false"
  />
  <EmailChangeModal
    v-else-if="editing && eligible && profile.emailAction === 'attach'"
    :visible="true"
    @close="editing = false"
    @saved="editing = false"
  />
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import BaseButton from '@/shared/components/base/BaseButton.vue'
import { useProfileStore } from '../store/profileStore'
import EmailChangeModal from './EmailChangeModal.vue'
import EmailVerificationModal from './EmailVerificationModal.vue'

const props = defineProps<{ eligible: boolean }>()
const { t } = useI18n()
const profile = useProfileStore()
const dialog = ref<HTMLDialogElement | null>(null)
const editing = ref(false)
// Memory belongs to the always-mounted root, not routes, storage or focus events.
let shown = false
const actionKey = computed(() =>
  profile.emailAction === 'attach' ? 'emailLink.attach' : 'emailLink.verify',
)
watch(
  () =>
    [
      props.eligible,
      profile.profile,
      profile.emailAction,
      dialog.value,
    ] as const,
  () => {
    if (!props.eligible || !profile.profile) {
      if (dialog.value?.open) dialog.value.close()
      editing.value = false
      return
    }
    if (profile.emailAction === 'change') {
      if (dialog.value?.open) dialog.value.close()
      editing.value = false
      return
    }
    if (!shown && dialog.value) {
      shown = true
      dialog.value.showModal()
    }
  },
  { immediate: true, flush: 'post' },
)
function dismiss() {
  dialog.value?.close()
}
function edit() {
  dismiss()
  editing.value = true
}
</script>

<style scoped lang="scss">
.email-link-reminder {
  margin: auto;
  width: min(440px, calc(100% - 32px));
  padding: 24px;
  border: 1px solid var(--ds-border);
  border-radius: var(--ds-radius-modal);
  background: var(--ds-surface);
  color: var(--ds-text);
  &::backdrop {
    background: var(--ds-scrim);
  }
  .base-btn {
    margin-top: 12px;
  }
}
</style>
